import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Navigate } from 'react-router-dom';
import EmbeddedLayout from '../../../components/admin/EmbeddedLayout';
import employeesController, { IEmployee } from '../../../Api/employeesController';
import { useUser } from '../../../hooks/useUser';

type FilterKey = 'all' | 'active' | 'inactive';

type EmployeeDraft = {
  firstName: string;
  lastName: string;
  isActive: boolean;
};

const emptyDraft = (): EmployeeDraft => ({ firstName: '', lastName: '', isActive: true });

const EmployeesPage = ({ embedded = false }: { embedded?: boolean }) => {
  const user = useUser();
  const isAdmin = user.hasPermission('employees.read');
  const authChecked = user.authReady;

  const [filter, setFilter] = useState<FilterKey>('all');
  const [rows, setRows] = useState<IEmployee[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [openDialog, setOpenDialog] = useState(false);
  const [mode, setMode] = useState<'create' | 'edit'>('create');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<EmployeeDraft>(emptyDraft());

  const load = useCallback(async () => {
    if (!authChecked || !isAdmin) return;

    setIsLoading(true);
    setError(null);

    const params = filter === 'all' ? undefined : { isActive: filter === 'active' };
    const res = await employeesController.getAllEmployees(params);

    if (!res.ok) {
      setRows([]);
      setError(res.message || 'Nie udało się pobrać pracowników');
      setIsLoading(false);
      return;
    }

    setRows(res.employees || []);
    setIsLoading(false);
  }, [authChecked, filter, isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setMode('create');
    setEditingId(null);
    setDraft(emptyDraft());
    setOpenDialog(true);
  };

  const openEdit = (e: IEmployee) => {
    setMode('edit');
    setEditingId(e._id);
    setDraft({
      firstName: e.firstName || '',
      lastName: e.lastName || '',
      isActive: typeof e.isActive === 'boolean' ? e.isActive : true,
    });
    setOpenDialog(true);
  };

  const save = async () => {
    const firstName = draft.firstName.trim();
    const lastName = draft.lastName.trim();

    if (!firstName || !lastName) {
      setError('Podaj imię i nazwisko');
      return;
    }

    setIsLoading(true);
    setError(null);

    if (mode === 'create') {
      const res = await employeesController.createEmployee({ firstName, lastName, isActive: draft.isActive });
      if (!res.ok) {
        setError(res.message || 'Nie udało się dodać pracownika');
        setIsLoading(false);
        return;
      }
      setOpenDialog(false);
      await load();
      return;
    }

    if (!editingId) {
      setError('Brak ID pracownika do edycji');
      setIsLoading(false);
      return;
    }

    const res = await employeesController.updateEmployee(editingId, { firstName, lastName, isActive: draft.isActive });
    if (!res.ok) {
      setError(res.message || 'Nie udało się zaktualizować pracownika');
      setIsLoading(false);
      return;
    }

    setOpenDialog(false);
    await load();
  };


  const columns = useMemo<GridColDef<IEmployee>[]>(
    () => [
      { field: 'lastName', headerName: 'Nazwisko', flex: 1, minWidth: 160 },
      { field: 'firstName', headerName: 'Imię', flex: 1, minWidth: 160 },
      {
        field: 'isActive',
        headerName: 'Aktywny',
        width: 120,
        valueGetter: (_value, row) => (row.isActive ? 'Tak' : 'Nie'),
      },
      {
        field: 'actions',
        headerName: 'Akcje',
        width: 140,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params.row;
          return (
            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
              <Stack direction="row" gap={1} alignItems="center">
                <Button size="small" variant="outlined" onClick={() => openEdit(row)}>
                  Edytuj
                </Button>
              </Stack>
            </Box>
          );
        },
      },
    ],
    []
  );

  if (!authChecked) {
    return (
      <EmbeddedLayout embedded={embedded} title="Pracownicy">
        <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" gap={1.5}>
            <CircularProgress size={22} />
            <Typography>Sprawdzam autoryzację…</Typography>
          </Stack>
        </Paper>
      </EmbeddedLayout>
    );
  }

  if (!isAdmin) return <Navigate to="/logowanie" replace />;

  return (
    <EmbeddedLayout embedded={embedded} title="Pracownicy">
      <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} justifyContent="space-between" gap={2}>
          <Box>
            <Typography variant="h6" fontWeight={900}>
              Pracownicy
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Zarządzanie pracownikami (dodawanie, edycja, usuwanie, aktywność)
            </Typography>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
            <Button variant="contained" onClick={openCreate}>
              Dodaj pracownika
            </Button>
            <Button variant="outlined" onClick={load} startIcon={isLoading ? <CircularProgress size={18} /> : undefined}>
              Odśwież
            </Button>
          </Stack>
        </Stack>

        {error ? (
          <Box sx={{ mt: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : null}

        <Divider sx={{ my: 2 }} />

        <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mb: 1.5 }}>
          <Chip
            label="Wszyscy"
            clickable
            variant={filter === 'all' ? 'filled' : 'outlined'}
            onClick={() => setFilter('all')}
          />
          <Chip
            label="Aktywni"
            clickable
            variant={filter === 'active' ? 'filled' : 'outlined'}
            onClick={() => setFilter('active')}
          />
          <Chip
            label="Nieaktywni"
            clickable
            variant={filter === 'inactive' ? 'filled' : 'outlined'}
            onClick={() => setFilter('inactive')}
          />
        </Stack>

        <Box sx={{ height: 520, width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(r) => r._id}
            loading={isLoading}
            disableRowSelectionOnClick
            pageSizeOptions={[25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25, page: 0 } },
            }}
          />
        </Box>
      </Paper>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>{mode === 'create' ? 'Dodaj pracownika' : 'Edytuj pracownika'}</DialogTitle>
        <DialogContent>
          <Stack gap={2} sx={{ mt: 1 }}>
            <TextField
              label="Imię"
              value={draft.firstName}
              onChange={(e) => setDraft((d) => ({ ...d, firstName: e.target.value }))}
              fullWidth
              autoFocus
            />
            <TextField
              label="Nazwisko"
              value={draft.lastName}
              onChange={(e) => setDraft((d) => ({ ...d, lastName: e.target.value }))}
              fullWidth
            />
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography fontWeight={800}>Aktywny</Typography>
                <Typography variant="caption" color="text.secondary">
                  Wyłącz, jeśli pracownik już nie pracuje.
                </Typography>
              </Box>
              <Switch
                checked={draft.isActive}
                onChange={(e) => setDraft((d) => ({ ...d, isActive: e.target.checked }))}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} variant="outlined">
            Anuluj
          </Button>
          <Button onClick={save} variant="contained" disabled={isLoading}>
            Zapisz
          </Button>
        </DialogActions>
      </Dialog>
    </EmbeddedLayout>
  );
};

export default EmployeesPage;
